<div class="main-view space-y-2 md:space-y-4">
    <app-page-surface-header titleIcon="clipboard"
        title="Audit Trail"
        subtitle="Super-admin portal sign-ins, system events, and merged activity."
    />

    <!-- Sessions and portal sign-ins are different record kinds; the API splits on entry_type. -->
    <div class="flex flex-wrap gap-1 border-b border-gray-200 dark:border-[#191e3a]" role="tablist">
        @for (tab of entryTabs; track tab.id) {
        <button
            type="button"
            role="tab"
            [attr.aria-selected]="entryType === tab.id"
            (click)="setEntryType(tab.id)"
            [class]="
                'px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ' +
                (entryType === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white')
            "
        >
            {{ tab.label }}
        </button>
        }
    </div>

    <app-list-module-surface
        [config]="listSurfaceConfig"
        [filters]="listFilters"
        [errorMessage]="error"
        [loading]="loading"
        [meta]="meta"
        [rowCount]="entries.length"
        (filtersChange)="onListFiltersChange($event)"
        (pageChange)="onListPageChange($event)"
    >
        <ng-container toolbarActions>
            @if (P.AUDIT_TRAIL_EXPORT | hasPermission) {
            <button
                type="button"
                class="btn inline-flex shrink-0 items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-none hover:bg-gray-50 dark:border-[#191e3a] dark:bg-[#0e1726] dark:text-white dark:hover:bg-white/5"
                (click)="onExport()"
                title="Export CSV"
            >
                <icon-download class="size-4" />
                Export CSV
            </button>
            }
        </ng-container>

        <app-audit-trail-table
            listTable
            [rows]="entries"
            [entryType]="entryType"
            [loading]="loading"
            (viewSession)="openDetail($event)"
            (viewLogin)="openLoginDetail($event)"
            (viewEvent)="openEventDetail($event)"
        />
    </app-list-module-surface>
</div>

<!-- ── Portal login detail modal ─────────────────────────────────────────── -->
<dialog
    *ngIf="detailLogin"
    open
    class="fixed inset-0 z-[200] m-0 flex h-full max-h-none w-full max-w-none items-center justify-center border-0 bg-transparent p-4 !mt-0"
    aria-labelledby="login-detail-title"
    (click)="closeLoginDetail()"
    (keydown.escape)="closeLoginDetail()"
>
    <div class="absolute inset-0 bg-black/50 backdrop-blur-[2px]" aria-hidden="true"></div>
    <div
        class="relative z-10 w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl dark:border dark:border-[#191e3a] dark:bg-[#0e1726]"
        (click)="$event.stopPropagation()" (keydown)="$event.stopPropagation()"
    >
        <div class="flex items-center justify-between gap-3 border-b border-gray-200 px-5 py-4 dark:border-[#191e3a]">
            <div>
                <h2 id="login-detail-title" class="text-base font-bold text-gray-900 dark:text-white">Portal sign-in</h2>
                <p class="text-xs text-gray-400">{{ loginUserEmail(detailLogin) }}</p>
            </div>
            <button
                type="button"
                class="flex size-9 shrink-0 items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-white/5"
                (click)="closeLoginDetail()"
                aria-label="Close"
            >
                <icon-x class="size-4" />
            </button>
        </div>
        <div class="space-y-0 divide-y divide-gray-200 p-5 text-sm dark:divide-gray-700">
            <div class="flex justify-between gap-4 py-2.5 first:pt-0">
                <span class="text-gray-500 dark:text-gray-400">User</span>
                <span class="font-medium text-gray-900 dark:text-white">{{ loginUserName(detailLogin) }}</span>
            </div>
            <div class="flex justify-between gap-4 py-2.5">
                <span class="text-gray-500 dark:text-gray-400">Status</span>
                <span class="inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold" [class]="loginStatusClass(detailLogin)">
                    {{ loginStatusLabel(detailLogin) }}
                </span>
            </div>
            <div class="flex justify-between gap-4 py-2.5">
                <span class="text-gray-500 dark:text-gray-400">When</span>
                <span class="font-medium text-gray-900 dark:text-white">
                    {{ entryDateDisplay(detailLogin.login_timestamp) }} {{ entryTimeDisplay(detailLogin.login_timestamp) }}
                </span>
            </div>
            <div class="flex justify-between gap-4 py-2.5">
                <span class="text-gray-500 dark:text-gray-400">IP address</span>
                <span class="font-mono text-gray-900 dark:text-white">{{ detailLogin.ip_address || '—' }}</span>
            </div>
            <div class="flex justify-between gap-4 py-2.5">
                <span class="text-gray-500 dark:text-gray-400">Browser</span>
                <span class="text-gray-900 dark:text-white">{{ detailLogin.browser || '—' }}</span>
            </div>
            <div class="flex justify-between gap-4 py-2.5">
                <span class="text-gray-500 dark:text-gray-400">Platform</span>
                <span class="text-gray-900 dark:text-white">{{ detailLogin.platform || '—' }}</span>
            </div>
            <div class="flex justify-between gap-4 py-2.5 last:pb-0">
                <span class="text-gray-500 dark:text-gray-400">Device</span>
                <span class="text-gray-900 dark:text-white">{{ detailLogin.device || '—' }}</span>
            </div>
        </div>
    </div>
</dialog>

<!-- System events reuse the Audit Log drawer so both pages read the same way. -->
<app-audit-entry-drawer [row]="detailEventRow" (closed)="closeEventDetail()" />
